import {
  buildForm,
  buildHtml,
  buildLink,
  Route,
  RouteSlotInput,
  ThemeProvider,
  WithStandardFrame,
} from '@luks/ui';
import { useLocation } from '@reach/router';
import { bind } from '@react-rxjs/core';
import { createSignal } from '@react-rxjs/utils';
import queryString from 'query-string';
import React, { PropsWithChildren } from 'react';
import { debounceTime } from 'rxjs';

import { useEmergencyMessageQuery } from '../../../generated/queries';
import { Chatbot } from '../chatbot';
import { formNavigate } from '../forms';
import { globMatch } from '../glob-match';
import { setInternalCookie } from '../internalCookie';
import { usePreviewQuery } from '../preview';
import { useSearchSuggestions } from '../search-suggestions';
import { useChildrenHospitalTheme } from '../use-children-hospital-theme';
import { useDrupalSettings } from '../use-drupal-settings';
import { useMenus } from '../use-menus';

const [queryInputChanges$, setQueryInput] = createSignal<string>();

const [useQueryInput] = bind(queryInputChanges$.pipe(debounceTime(200)), '');

const SearchForm = buildForm<{ query: string }>({
  initialValues: { query: '' },
  onSubmit: formNavigate('/site-search'),
  onChange: (values) => values.query && setQueryInput(values.query),
  useInitialValues() {
    const query = new URLSearchParams(useLocation().search).get('query') || '';
    return { query };
  },
});

const createUseHeader: (
  editUrl: string | null,
) => RouteSlotInput<typeof WithStandardFrame, 'header'> = (editUrl) =>
  function useHeader() {
    const {
      menus: { meta, main },
    } = useMenus();

    const { status, suggestions } = useSearchSuggestions(useQueryInput());

    const { siteName } = useDrupalSettings();

    // The last item in the meta menu is the emergency link, we apply it hardcoded.
    const emergencyLink = meta.items.pop();

    return [
      {
        SearchForm,
        mainMenuItems: main.items,
        metaMenuItems: meta.items,
        EmergencyLink: buildLink({
          href: emergencyLink?.Link.href || '/notfall',
        }),
        EditLink: editUrl
          ? buildLink({ href: editUrl, target: '_blank' })
          : null,
        emergencyLinkText: emergencyLink?.title || 'Notfall',
        suggestions,
        Logo: {
          Link: buildLink({ href: '/' }),
          title: `Startseite des ${siteName}`,
        },
      },
      status,
    ];
  };

export const useEmergencyMessage: RouteSlotInput<
  typeof WithStandardFrame,
  'emergencyMessage'
> = () => {
  const { data } = useEmergencyMessageQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchInterval: 1000 * 60 * 5,
  });
  const title = data?.drupalSettings.emergencyMessage?.title;
  const textHtml = data?.drupalSettings.emergencyMessage?.textHtml;
  return [
    {
      title,
      Content: textHtml ? buildHtml(textHtml) : undefined,
    },
    200,
  ];
};

export const useFooter: RouteSlotInput<
  typeof WithStandardFrame,
  'footer'
> = () => {
  const {
    menus: { legal, footer },
  } = useMenus();
  const { siteName, siteSlogan } = useDrupalSettings();
  return [
    {
      siteName,
      siteSlogan,
      copyMenus: legal.items,
      menus: footer.items.map((column) => ({
        title: column.title,
        Link: column.Link,
        items: column.children,
      })),
      Logo: {
        Link: buildLink({ href: '/' }),
        title: siteName,
      },
    },
    200,
  ];
};

export function StandardFrame({
  children,
  editUrl,
  forceChildrenHospitalTheme,
}: PropsWithChildren<{
  editUrl: string | null;
  forceChildrenHospitalTheme?: boolean;
}>) {
  const { search, pathname } = useLocation();
  const parsedQueryString = queryString.parse(search) as {
    preview?: string;
    'display-internal'?: string;
  };
  const preview = usePreviewQuery() && !parsedQueryString.preview;

  if (parsedQueryString['display-internal']) {
    setInternalCookie(true);
  }

  const childrenHospitalThemePaths = useChildrenHospitalTheme();
  const kinderspital =
    forceChildrenHospitalTheme ||
    !!childrenHospitalThemePaths.find((pattern) =>
      globMatch(pathname, pattern),
    );

  return (
    <ThemeProvider
      theme={
        (process.env.GATSBY_LUKS_THEME as 'luzern' | 'nidwalden' | 'zskr') ||
        'luzern'
      }
      groupTheme={kinderspital ? 'kinderspital' : undefined}
    >
      <Route
        definition={WithStandardFrame}
        input={{
          header: createUseHeader(
            preview && editUrl
              ? `${process.env.GATSBY_DRUPAL_URL}${editUrl}`
              : null,
          ),
          emergencyMessage: useEmergencyMessage,
          footer: useFooter,
        }}
        intl={{ defaultLocale: 'de', locale: 'de' }}
      >
        {children}
      </Route>
      <Chatbot />
    </ThemeProvider>
  );
}
